import React from "react";
import { TableButton } from "./NewTable";
import { formatDate } from "../utils/date";

interface CalendarProps {
  todaysDate: Date;
  handleDayClick: (date: Date) => void;
  startDate?: string;
  endDate?: string;
  onDateRangeChange?: (start: string, end: string) => void;
}

const Calendar: React.FC<CalendarProps> = ({
  todaysDate,
  handleDayClick,
  startDate,
  endDate,
  onDateRangeChange,
}) => {
  const [currentDate, setCurrentDate] = React.useState<Date>(new Date());
  const [tempStartDate, setTempStartDate] = React.useState<string | undefined>(
    startDate
  );
  const [tempEndDate, setTempEndDate] = React.useState<string | undefined>(
    endDate
  );

  React.useEffect(() => {
    if (todaysDate) {
      setCurrentDate(todaysDate);
    }
  }, [todaysDate]);

  React.useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, [startDate, endDate]);

  const addDays = (date: Date, days: number) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const handleDateSelection = (date: Date) => {
    const selectedDate = formatDate(date);
    const endDate = formatDate(addDays(date, 29));

    setTempStartDate(selectedDate);
    setTempEndDate(endDate);

    if (onDateRangeChange) {
      onDateRangeChange(selectedDate, endDate);
    }

    handleDayClick(date);
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay();
  };

  const getPrevMonthDays = (year: number, month: number) => {
    const firstDay = getFirstDayOfMonth(year, month);
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevMonthYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = getDaysInMonth(prevMonthYear, prevMonth);

    const days = [];
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        day: daysInPrevMonth - i,
        current: false,
        date: new Date(prevMonthYear, prevMonth, daysInPrevMonth - i),
      });
    }
    return days;
  };

  const getCurrentMonthDays = (year: number, month: number) => {
    const daysInMonth = getDaysInMonth(year, month);
    const days = [];
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        current: true,
        date: new Date(year, month, i),
      });
    }
    return days;
  };

  const getNextMonthDays = (
    year: number,
    month: number,
    currentMonthDays: any[]
  ) => {
    const totalDaysShown = 42;
    const prevMonthDays = getPrevMonthDays(year, month);
    const remainingDays =
      totalDaysShown - prevMonthDays.length - currentMonthDays.length;

    const nextMonth = month === 11 ? 0 : month + 1;
    const nextMonthYear = month === 11 ? year + 1 : year;

    const days = [];
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        current: false,
        date: new Date(nextMonthYear, nextMonth, i),
      });
    }
    return days;
  };

  const getAllDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const prevDays = getPrevMonthDays(year, month);
    const currentDays = getCurrentMonthDays(year, month);
    const nextDays = getNextMonthDays(year, month, currentDays);

    return [...prevDays, ...currentDays, ...nextDays];
  };

  const formatMonthYear = (date: Date) => {
    return date.toLocaleString("default", { month: "long", year: "numeric" });
  };

  const isSelected = (date: Date) => {
    return formatDate(date) === formatDate(currentDate);
  };

  const isInBillingCycle = (date: Date) => {
    if (!startDate || !endDate) return false;
    const dateStr = formatDate(date);
    return dateStr >= startDate && dateStr <= endDate;
  };

  const isBeforeAllowedRange = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const oneMonthAgo = new Date(today);
    oneMonthAgo.setMonth(today.getMonth() - 1);
    return date < oneMonthAgo;
  };

  const calendarDays = getAllDays();

  return (
    <div
      style={{
        border: "1px solid #E5E7EB",
        borderRadius: "8px",
        padding: "0.5rem",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0.5rem 20px",
        }}
      >
        <TableButton
          icon="chevron-left"
          tooltip="Previous"
          onClick={() =>
            setCurrentDate(
              new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
            )
          }
        />
        <div
          style={{ fontWeight: "600", fontSize: "18px", fontFamily: "Roboto" }}
        >
          {formatMonthYear(currentDate)}
        </div>
        <TableButton
          icon="chevron-right"
          tooltip="Next"
          onClick={() =>
            setCurrentDate(
              new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
            )
          }
        />
      </div>

      {/* Days of Week */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          textAlign: "center",
          fontWeight: "400",
          color: "#6B7280",
          padding: "8px 0",
          fontSize: "15px",
        }}
      >
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "2px",
        }}
      >
        {calendarDays.map((day, index) => {
          const isCurrentDay = day.current;
          const isSelectedDay = isSelected(day.date);
          const isInCycle = isInBillingCycle(day.date);
          const isBeforeRange = isBeforeAllowedRange(day.date);

          const isStartDate =
            tempStartDate && formatDate(day.date) === tempStartDate;
          const isEndDate = tempEndDate && formatDate(day.date) === tempEndDate;
          const isInRange = isInCycle && !isStartDate && !isEndDate;

          return (
            <div
              key={index}
              onClick={() => !isBeforeRange && handleDateSelection(day.date)}
              style={{
                width: "100%",
                height: "38px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isBeforeRange ? "not-allowed" : "pointer",
                color: isCurrentDay ? "#111827" : "#9CA3AF",
                opacity: isBeforeRange ? 0.4 : 1,
                backgroundColor:
                  isInRange || isStartDate || isEndDate
                    ? "#E5EDFF"
                    : "transparent",
              }}
            >
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  fontSize: "16px",
                  fontWeight: "400",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isSelectedDay ? "#E5EDFF" : "transparent",
                  color:
                    isSelectedDay || isStartDate || isEndDate || isInRange
                      ? "#2563EB"
                      : isCurrentDay
                      ? "#111827"
                      : "#9CA3AF",
                }}
              >
                {day.day}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;
